'use client';

import { Media } from '@/components/common/Media/Media';
import { type ProductSimple } from '@/domain/products/types';
import { useMediaQuery } from '@/hooks';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { IconButton } from '@andritzot/metris-web-ui/inputs/icon-button';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { Drawer } from '@andritzot/metris-web-ui/navigation/drawer';
import {
  CloseOutlined,
  FilterListOutlined,
  KeyboardDoubleArrowLeftOutlined,
  KeyboardDoubleArrowRightOutlined,
} from '@mui/icons-material';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { useCallback } from 'react';
import { toast } from 'react-toastify';

import { useSolutionStore } from '../../store/solution';
import { type FilterList, type Filters } from '../../types';
import ProductFilter from './ProductFilter';

interface SideFilterProps {
  filteredProducts: ProductSimple[];
  filtersValue: Filters[];
  filterList: FilterList;
  isLoggedIn: boolean;
}

const SideFilter = ({ filteredProducts, filtersValue, filterList, isLoggedIn }: SideFilterProps) => {
  const isSidebarOpen = useSolutionStore((s) => s.isSidebarOpen);
  const filters = useSolutionStore((s) => s.filters);
  const { isMobile } = useMediaQuery();

  const handleCloseSidebar = () => useSolutionStore.setState({ isSidebarOpen: false });
  const handleToggleSidebar = () => useSolutionStore.setState({ isSidebarOpen: !isSidebarOpen });

  const shareFilters = useCallback(async () => {
    const query = filters.map((filter) => `q=${filter.id};${filter.group}`).join('&');
    const url = `${globalThis.location.origin}?${query}`;

    if (url.length > 2000) {
      toast.error('Too many filters. Max size has been exceeded');
      return;
    }

    await navigator.clipboard.writeText(url);
    toast.success('Copied to clipboard');
  }, [filters]);

  const filterContent = (
    <ProductFilter
      filterList={filterList}
      filteredProducts={filteredProducts}
      filtersValue={filtersValue}
      isLoggedIn={isLoggedIn}
    />
  );

  return (
    <Box
      data-testid="side-filter"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {isMobile ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
          <IconButton size="small" variant="outlined" onClick={handleToggleSidebar} aria-label="Open filters">
            <FilterListOutlined />
          </IconButton>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 4,
            flexShrink: 0,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <IconButton
              size="small"
              variant="outlined"
              onClick={handleToggleSidebar}
              aria-label={isSidebarOpen ? 'Collapse filters' : 'Expand filters'}
            >
              {isSidebarOpen ? <KeyboardDoubleArrowLeftOutlined /> : <KeyboardDoubleArrowRightOutlined />}
            </IconButton>
            <Typography variant="cardHeading" sx={{ flexShrink: 0 }}>
              Filters
            </Typography>
          </Box>

          <IconButton
            variant="outlined"
            onClick={shareFilters}
            disabled={filters.length === 0}
            color="primary"
            aria-label="Share filters"
            size="small"
          >
            <ShareOutlinedIcon />
          </IconButton>
        </Box>
      )}
      {!isMobile && isSidebarOpen && (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Media greaterThanOrEqual="md" style={{ height: '100%' }}>
            <Box sx={{ height: '100%' }}>{filterContent}</Box>
          </Media>
        </Box>
      )}
      <Drawer
        anchor="left"
        open={isMobile && isSidebarOpen}
        onClose={handleCloseSidebar}
        PaperProps={{ sx: { width: '85%', maxWidth: 360 } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              flexShrink: 0,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="cardHeading">Filters</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                variant="outlined"
                onClick={shareFilters}
                disabled={filters.length === 0}
                color="primary"
                aria-label="Share filters"
                size="small"
              >
                <ShareOutlinedIcon />
              </IconButton>
              <IconButton size="small" onClick={handleCloseSidebar} aria-label="Close filters">
                <CloseOutlined />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>{filterContent}</Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default SideFilter;
